import React, { useState, useEffect } from 'react';
import { CATEGORIES } from '../../utils/categoryConstants';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  IconButton,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControl,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  FormGroup,
  Checkbox,
  Divider,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar
} from '@mui/material';
import {
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  CurrencyPound as PricingIcon,
  Inventory as ProductsIcon,
  Extension as AddonsIcon,
  Settings as SettingsIcon,
  AccessTime as TimeIcon,
  Save as SaveIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import NotificationSettings from '../garage-admin/NotificationSettings';

const GarageSettingsModal = ({ open, onClose, garage }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  
  // Settings state
  const [settings, setSettings] = useState({
    openingHours: {
      monday: { enabled: true, start: '09:00', end: '17:00' },
      tuesday: { enabled: true, start: '09:00', end: '17:00' },
      wednesday: { enabled: true, start: '09:00', end: '17:00' },
      thursday: { enabled: true, start: '09:00', end: '17:00' },
      friday: { enabled: true, start: '09:00', end: '17:00' },
      saturday: { enabled: true, start: '09:00', end: '17:00' },
      sunday: { enabled: false, start: '', end: '' }
    },
    lunchHours: { enabled: true, start: '12:00', end: '13:00', days: [] },
    selectedProducts: [], // Products selected for this garage with custom pricing
    selectedAddons: [], // Addons selected for this garage with custom pricing
    pricingConfig: {
      discount_percentage: 0.0,
      weekend_multiplier: 1.0,
      urgent_booking_multiplier: 1.0
    },
    bookingConfig: {
      accept_booking: true,
      advance_booking_days: 30,
      cancellation_window_hours: 24,
      lunch_hours: { enabled: true, start: '12:00', end: '13:00' }
    },
    notificationConfigs: []
  });

  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableAddons, setAvailableAddons] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedIncludes, setExpandedIncludes] = useState({});

  // Transform backend format to frontend format
  const transformBackendSettings = (backendData) => {
    const dayMapping = {
      'mon': 'monday',
      'tue': 'tuesday', 
      'wed': 'wednesday',
      'thu': 'thursday',
      'fri': 'friday',
      'sat': 'saturday',
      'sun': 'sunday'
    };

    const transformedOpeningHours = {};
    
    // Transform opening hours from backend format to frontend format
    if (backendData.opening_hours || backendData.openingHours) {
      const openingHours = backendData.opening_hours || backendData.openingHours;
      Object.entries(openingHours).forEach(([day, timeString]) => {
        const fullDay = dayMapping[day] || day;
        if (timeString === 'closed' || !timeString) {
          transformedOpeningHours[fullDay] = { enabled: false, start: '', end: '' };
        } else {
          const [start, end] = timeString.split('-');
          transformedOpeningHours[fullDay] = { 
            enabled: true, 
            start: start || '09:00', 
            end: end || '17:00' 
          };
        }
      });
    }

    // Ensure all days are present with default values
    const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    allDays.forEach(day => {
      if (!transformedOpeningHours[day]) {
        // Set default opening hours (09:00-17:00) for weekdays, closed for weekends
        const isWeekend = day === 'saturday' || day === 'sunday';
        transformedOpeningHours[day] = isWeekend 
          ? { enabled: false, start: '09:00', end: '17:00' }
          : { enabled: true, start: '09:00', end: '17:00' };
      }
    });

    // Transform products from backend format
    const transformedProducts = (backendData.products || backendData.selected_products || []).map(item => ({
      id: item.product_id || item.id,
      name: item.name || item.product?.name,
      description: item.description || item.product?.description,
      category: item.category || item.product?.category,
      default_price: item.default_price || item.product?.default_price || 0,
      duration_minutes: item.custom_duration !== undefined && item.custom_duration !== null ? item.custom_duration : (item.duration_minutes || 0),
      custom_price: item.custom_price || item.base_price || item.product?.default_price || 0,
      enabled: item.is_enabled || false,
      is_active: item.product?.is_active !== undefined ? item.product.is_active : true,
      includes: item.product?.includes || []
    }));

    // Transform addons from backend format
    const transformedAddons = (backendData.addons || backendData.selected_addons || []).map(item => ({
      id: item.addon_id || item.id,
      name: item.name || item.addon?.name,
      description: item.description || item.addon?.description,
      default_price: item.default_price || item.addon?.default_price || 0,
      duration_minutes: item.custom_duration !== undefined && item.custom_duration !== null ? item.custom_duration : (item.duration_minutes || 0),
      custom_price: item.custom_price || item.base_price || item.addon?.default_price || 0,
      enabled: item.is_enabled || false,
      is_active: item.addon?.is_active !== undefined ? item.addon.is_active : true,
      includes: item.addon?.includes || []
    }));

    // Transform lunch hours from backend format
    const lunchHours = backendData.lunch_hours || backendData.lunchHours || { enabled: true, start: '12:00', end: '13:00', days: [] };

    return {
      garage_id: backendData.garage_id,
      openingHours: transformedOpeningHours,
      lunchHours: lunchHours,
      products: transformedProducts,
      addons: transformedAddons,
      pricingConfig: backendData.pricing_config || {
        discount_percentage: 0.0,
        weekend_multiplier: 1.0,
        urgent_booking_multiplier: 1.0
      },
      bookingConfig: backendData.booking_config || {
        accept_booking: true,
        advance_booking_days: 30,
        cancellation_window_hours: 24
      },
      notificationConfigs: backendData.notification_configs || []
    };
  };

  // Transform frontend format to backend format
  const transformFrontendSettings = (frontendData) => {
    const dayMapping = {
      'monday': 'mon',
      'tuesday': 'tue',
      'wednesday': 'wed',
      'thursday': 'thu',
      'friday': 'fri',
      'saturday': 'sat',
      'sunday': 'sun'
    };

    const transformedOpeningHours = {};
    
    Object.entries(frontendData.openingHours || {}).forEach(([day, config]) => {
      const shortDay = dayMapping[day] || day;
      if (config && config.enabled && config.start && config.end) {
        transformedOpeningHours[shortDay] = `${config.start}-${config.end}`;
      } else {
        transformedOpeningHours[shortDay] = 'closed';
      }
    });

    // Clean up products and addons data to match backend schema
    const cleanProducts = (frontendData.products || []).map(product => ({
      product_id: product.id,
      is_enabled: product.enabled || false,
      base_price: parseFloat(product.custom_price) || parseFloat(product.default_price) || 0,
      custom_duration: parseInt(product.duration_minutes) || 0,
      product: {
        id: product.id,
        name: product.name || '',
        description: product.description || '',
        category: product.category || '',
        default_price: parseFloat(product.default_price) || 0,
        duration_minutes: parseInt(product.duration_minutes) || 0,
        is_active: product.is_active !== undefined ? product.is_active : true,
        includes: product.includes || []
      }
    }));

    const cleanAddons = (frontendData.addons || []).map(addon => ({
      addon_id: addon.id,
      is_enabled: addon.enabled || false,
      base_price: parseFloat(addon.custom_price) || parseFloat(addon.default_price) || 0,
      custom_duration: parseInt(addon.duration_minutes) || 0,
      addon: {
        id: addon.id,
        name: addon.name || '',
        description: addon.description || '',
        default_price: parseFloat(addon.default_price) || 0,
        duration_minutes: parseInt(addon.duration_minutes) || 0,
        is_active: addon.is_active !== undefined ? addon.is_active : true,
        includes: addon.includes || []
      }
    }));

    return {
      opening_hours: transformedOpeningHours,
      products: cleanProducts,
      addons: cleanAddons,
      pricing_config: frontendData.pricingConfig || {
        discount_percentage: 0.0,
        weekend_multiplier: 1.0,
        urgent_booking_multiplier: 1.0
      },
      booking_config: {
        accept_booking: frontendData.bookingConfig?.accept_booking !== undefined ? frontendData.bookingConfig.accept_booking : true,
        advance_booking_days: frontendData.bookingConfig?.advance_booking_days || 30,
        cancellation_window_hours: frontendData.bookingConfig?.cancellation_window_hours || 24
      },
      lunch_hours: frontendData.lunchHours || { enabled: true, start: '12:00', end: '13:00' },
      notification_configs: frontendData.notificationConfigs || []
    };
  };

  useEffect(() => {
    if (garage?.id && open) {
      loadGarageSettings();
    }
  }, [garage?.id, open]);

  const loadGarageSettings = async () => {
    if (!garage?.id) return;
    
    setLoading(true);
    try {
      // Get garage settings and all available products/addons
      const [settingsResponse, productsResponse, addonsResponse] = await Promise.all([
        apiService.getGarageSettings(garage.id),
        // Get all available products (active only)
        apiService.getActiveProducts(),
        // Get all available addons (active only)
        apiService.getActiveAddons()
      ]);

      if (settingsResponse.data) {
        // Transform backend format to frontend format
        const transformedSettings = transformBackendSettings(settingsResponse.data);
        setSettings(prev => ({
          ...prev,
          ...transformedSettings
        }));
      }

      // All available products and addons for selection
      setAvailableProducts(productsResponse.data || []);
      setAvailableAddons(addonsResponse.data || []);
    } catch (error) {
      console.error('Error loading garage settings:', error);
      setError('Failed to load garage settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const validateSettings = () => {
    const errors = [];
    
    // Validate pricing config
    if (settings.pricingConfig.weekend_multiplier <= 0) {
      errors.push('Weekend multiplier must be greater than 0');
    }
    if (settings.pricingConfig.urgent_booking_multiplier <= 0) {
      errors.push('Urgent booking multiplier must be greater than 0');
    }
    if (settings.pricingConfig.discount_percentage < 0 || settings.pricingConfig.discount_percentage > 100) {
      errors.push('Discount percentage must be between 0 and 100');
    }
    
    // Validate booking config
    if (settings.bookingConfig.advance_booking_days <= 0) {
      errors.push('Advance booking days must be greater than 0');
    }
    if (settings.bookingConfig.cancellation_window_hours <= 0) {
      errors.push('Cancellation window hours must be greater than 0');
    }
    
    // Validate enabled products
    const enabledProducts = settings.products.filter(p => p.enabled);
    enabledProducts.forEach(product => {
      if (product.custom_price === '' || product.custom_price <= 0) {
        errors.push(`Product "${product.name}" price must be greater than 0`);
      }
      if (product.duration_minutes === '' || product.duration_minutes <= 0) {
        errors.push(`Product "${product.name}" duration must be greater than 0`);
      }
    });
    
    // Validate enabled addons
    const enabledAddons = settings.addons.filter(a => a.enabled);
    enabledAddons.forEach(addon => {
      if (addon.custom_price === '' || addon.custom_price < 0) {
        errors.push(`Addon "${addon.name}" price cannot be negative`);
      }
      if (addon.duration_minutes === '' || addon.duration_minutes < 0) {
        errors.push(`Addon "${addon.name}" duration cannot be negative`);
      }
    });
    
    return errors;
  };

  const handleSave = async () => {
    if (!garage?.id) return;
    
    // Validate settings before saving
    const validationErrors = validateSettings();
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      return;
    }
    
    setSaving(true);
    try {
      // Use generic API endpoint - backend handles role-based access control
      const backendSettings = transformFrontendSettings(settings);
      
      console.log('Sending settings to backend:', backendSettings);
      
      await apiService.updateGarageSettings(garage.id, backendSettings);
      setSuccess('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      console.error('Error response:', error.response?.data);
      setError(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleOpeningHourChange = (day, field, value) => {
    setSettings(prev => ({
      ...prev,
      openingHours: {
        ...prev.openingHours,
        [day]: {
          ...prev.openingHours[day],
          [field]: value
        }
      }
    }));
  };

  const handleLunchHourChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      lunchHours: {
        ...prev.lunchHours,
        [field]: value
      }
    }));
  };

  const handleProductToggle = (productId) => {
    setSettings(prev => {
      const existingProduct = prev.products.find(p => p.id === productId);
      const product = availableProducts.find(p => p.id === productId);
      
      if (existingProduct) {
        // Toggle enabled state
        return {
          ...prev,
          products: prev.products.map(p => 
            p.id === productId 
              ? { ...p, enabled: !p.enabled }
              : p
          )
        };
      } else {
        // Add new product with enabled state by default
        return {
          ...prev,
          products: [...prev.products, { 
            ...product, 
            enabled: true, 
            custom_price: product.default_price,
            duration_minutes: product.duration_minutes
          }]
        };
      }
    });
  };

  const handleAddonToggle = (addonId) => {
    setSettings(prev => {
      const existingAddon = prev.addons.find(a => a.id === addonId);
      const addon = availableAddons.find(a => a.id === addonId);
      
      if (existingAddon) {
        // Toggle enabled state
        return {
          ...prev,
          addons: prev.addons.map(a => 
            a.id === addonId 
              ? { ...a, enabled: !a.enabled }
              : a
          )
        };
      } else {
        // Add new addon with enabled state by default
        return {
          ...prev,
          addons: [...prev.addons, { 
            ...addon, 
            enabled: true, 
            custom_price: addon.default_price,
            duration_minutes: addon.duration_minutes
          }]
        };
      }
    });
  };

  const handlePricingConfigChange = (field, value) => {
    // Allow free editing during onChange - no validation here
    setSettings(prev => ({
      ...prev,
      pricingConfig: {
        ...prev.pricingConfig,
        [field]: value
      }
    }));
  };


  const handleBookingConfigChange = (field, value) => {
    // Allow free editing during onChange - no validation here
    setSettings(prev => ({
      ...prev,
      bookingConfig: {
        ...prev.bookingConfig,
        [field]: value
      }
    }));
  };


  const renderOpeningHoursTab = () => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
          Opening Hours Configuration
        </Typography>
        
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {days.map((day, index) => (
            <Grid item xs={12} sm={6} lg={4} key={day}>
              <Card variant="outlined" sx={{ 
                backgroundColor: settings.openingHours[day].enabled ? 'background.paper' : 'action.hover',
                border: '1px solid',
                borderColor: settings.openingHours[day].enabled ? 'primary.light' : 'divider',
                height: '100%'
              }}>
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' }, 
                    justifyContent: 'space-between', 
                    mb: 2,
                    gap: { xs: 1, sm: 0 }
                  }}>
                    <Typography variant="subtitle1" sx={{ 
                      fontWeight: 500,
                      fontSize: { xs: '0.95rem', sm: '1rem' }
                    }}>
                      {dayLabels[index]}
                    </Typography>
                    <Switch
                      checked={settings.openingHours[day].enabled}
                      onChange={(e) => handleOpeningHourChange(day, 'enabled', e.target.checked)}
                      color="primary"
                      size={isMobile ? "small" : "medium"}
                    />
                  </Box>
                  
                  {settings.openingHours[day].enabled && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1.5, sm: 2 }, 
                      alignItems: { xs: 'stretch', sm: 'center' }
                    }}>
                      <TextField
                        label="Start"
                        type="time"
                        value={settings.openingHours[day].start}
                        onChange={(e) => handleOpeningHourChange(day, 'start', e.target.value)}
                        size="small"
                        sx={{ 
                          flex: 1,
                          minWidth: { xs: '100%', sm: '110px' }
                        }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: !isMobile && (
                            <InputAdornment position="start">
                              <TimeIcon color="primary" sx={{ fontSize: '1rem' }} />
                            </InputAdornment>
                          )
                        }}
                      />
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          display: { xs: 'none', sm: 'block' },
                          alignSelf: 'center'
                        }}
                      >
                        to
                      </Typography>
                      <TextField
                        label="End"
                        type="time"
                        value={settings.openingHours[day].end}
                        onChange={(e) => handleOpeningHourChange(day, 'end', e.target.value)}
                        size="small"
                        sx={{ 
                          flex: 1,
                          minWidth: { xs: '100%', sm: '110px' }
                        }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: !isMobile && (
                            <InputAdornment position="start">
                              <TimeIcon color="primary" sx={{ fontSize: '1rem' }} />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
          Lunch Break Configuration
        </Typography>
        
        <Card variant="outlined" sx={{ 
          backgroundColor: settings.lunchHours.enabled ? 'background.paper' : 'action.hover',
          border: '1px solid',
          borderColor: settings.lunchHours.enabled ? 'primary.light' : 'divider'
        }}>
          <CardContent>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.lunchHours.enabled}
                  onChange={(e) => handleLunchHourChange('enabled', e.target.checked)}
                  color="primary"
                />
              }
              label="Enable Lunch Break"
              sx={{ mb: 2 }}
            />
            
            {settings.lunchHours.enabled && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1.5, sm: 2 }, 
                alignItems: { xs: 'stretch', sm: 'center' },
                mt: 2 
              }}>
                <TextField
                  label="Start Time"
                  type="time"
                  value={settings.lunchHours.start}
                  onChange={(e) => handleLunchHourChange('start', e.target.value)}
                  size="small"
                  sx={{ 
                    flex: 1,
                    maxWidth: { xs: '100%', sm: '150px' }
                  }}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: !isMobile && (
                      <InputAdornment position="start">
                        <TimeIcon color="primary" sx={{ fontSize: '1rem' }} />
                      </InputAdornment>
                    )
                  }}
                />
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    display: { xs: 'none', sm: 'block' },
                    alignSelf: 'center'
                  }}
                >
                  to
                </Typography>
                <TextField
                  label="End Time"
                  type="time"
                  value={settings.lunchHours.end}
                  onChange={(e) => handleLunchHourChange('end', e.target.value)}
                  size="small"
                  sx={{ 
                    flex: 1,
                    maxWidth: { xs: '100%', sm: '150px' }
                  }}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: !isMobile && (
                      <InputAdornment position="start">
                        <TimeIcon color="primary" sx={{ fontSize: '1rem' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderProductsTab = () => {
    const categories = ['all', ...CATEGORIES];
    
    const filteredProducts = selectedCategory === 'all' 
      ? (Array.isArray(availableProducts) ? availableProducts : [])
      : (Array.isArray(availableProducts) ? availableProducts.filter(product => product.category === selectedCategory) : []);

    const toggleIncludes = (productId) => {
      setExpandedIncludes(prev => ({
        ...prev,
        [productId]: !prev[productId]
      }));
    };

    const renderIncludes = (includes, productId) => {
      if (!includes || includes.length === 0) return null;
      
      const isExpanded = expandedIncludes[productId];
      const displayIncludes = isExpanded ? includes : includes.slice(0, 2);
      
      return (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="primary.main" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
            What's included:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            {displayIncludes.map((include, index) => (
              <Chip
                key={index}
                label={include}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ 
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  borderColor: 'primary.main',
                  color: 'primary.main'
                }}
              />
            ))}
            {includes.length > 2 && (
              <Chip
                label={isExpanded ? 'Less' : `+${includes.length - 2} more...`}
                size="small"
                variant="outlined"
                color="info"
                onClick={() => toggleIncludes(productId)}
                sx={{ 
                  fontSize: '0.75rem', 
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'info.50' }
                }}
              />
            )}
          </Box>
        </Box>
      );
    };

    const renderMobileCard = (product) => {
      const isEnabled = settings.products.some(p => p.id === product.id && p.enabled);
      const enabledProduct = settings.products.find(p => p.id === product.id);
      
      return (
        <Card key={product.id} variant="outlined" sx={{ 
          mb: 2,
          backgroundColor: isEnabled ? 'primary.50' : 'background.paper',
          border: '1px solid',
          borderColor: isEnabled ? 'primary.light' : 'divider',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: theme.shadows[4],
            borderColor: isEnabled ? 'primary.main' : 'primary.light'
          }
        }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, color: 'white' }}>
                <ProductsIcon sx={{ color: 'white' }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {product.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {product.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  £{product.default_price} • {product.duration_minutes} mins
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip
                    label={product.category}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    label={isEnabled ? 'Enabled' : 'Disabled'}
                    color={isEnabled ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                {renderIncludes(product.includes, product.id)}
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
              <Switch
                checked={isEnabled}
                onChange={() => handleProductToggle(product.id)}
                color="primary"
              />
            </Box>
            
            {isEnabled && (
              <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Custom Price"
                  type="number"
                  value={enabledProduct?.custom_price !== undefined ? enabledProduct.custom_price : product.default_price}
                  onChange={(e) => {
                    const value = e.target.value === '' ? '' : parseFloat(e.target.value);
                    const updatedProducts = settings.products.map(p =>
                      p.id === product.id ? { ...p, custom_price: value } : p
                    );
                    setSettings(prev => ({ ...prev, products: updatedProducts }));
                  }}
                  onBlur={(e) => {
                    const value = e.target.value === '' ? product.default_price : parseFloat(e.target.value);
                    const parsedValue = isNaN(value) ? product.default_price : (value <= 0 ? product.default_price : value);
                    const updatedProducts = settings.products.map(p =>
                      p.id === product.id ? { ...p, custom_price: parsedValue } : p
                    );
                    setSettings(prev => ({ ...prev, products: updatedProducts }));
                  }}
                  size="small"
                  sx={{ flex: 1, minWidth: isMobile ? '120px' : '250px' }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Duration (mins)"
                  type="number"
                  value={enabledProduct?.duration_minutes !== undefined ? enabledProduct.duration_minutes : product.duration_minutes}
                  onChange={(e) => {
                    const value = e.target.value === '' ? '' : parseInt(e.target.value);
                    const updatedProducts = settings.products.map(p =>
                      p.id === product.id ? { ...p, duration_minutes: value } : p
                    );
                    setSettings(prev => ({ ...prev, products: updatedProducts }));
                  }}
                  onBlur={(e) => {
                    const value = e.target.value === '' ? product.duration_minutes : parseInt(e.target.value);
                    const parsedValue = isNaN(value) ? product.duration_minutes : (value <= 0 ? product.duration_minutes : value);
                    const updatedProducts = settings.products.map(p =>
                      p.id === product.id ? { ...p, duration_minutes: parsedValue } : p
                    );
                    setSettings(prev => ({ ...prev, products: updatedProducts }));
                  }}
                  size="small"
                  sx={{ flex: 1, minWidth: isMobile ? '120px' : '250px' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">mins</InputAdornment>
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      );
    };

    const renderDesktopTable = () => (
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Product Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.map((product) => {
              const isEnabled = settings.products.some(p => p.id === product.id && p.enabled);
              const enabledProduct = settings.products.find(p => p.id === product.id);
              
              return (
                <TableRow key={product.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                        <ProductsIcon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2">{product.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.description}
                        </Typography>
                        {renderIncludes(product.includes, product.id)}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={product.category} 
                      size="small" 
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {isEnabled ? (
                      <TextField
                        type="number"
                        value={enabledProduct?.custom_price !== undefined ? enabledProduct.custom_price : product.default_price}
                        onChange={(e) => {
                          const value = e.target.value === '' ? '' : parseFloat(e.target.value);
                          const updatedProducts = settings.products.map(p =>
                            p.id === product.id ? { ...p, custom_price: value } : p
                          );
                          setSettings(prev => ({ ...prev, products: updatedProducts }));
                        }}
                        onBlur={(e) => {
                          const value = e.target.value === '' ? product.default_price : parseFloat(e.target.value);
                          const parsedValue = isNaN(value) ? product.default_price : (value <= 0 ? product.default_price : value);
                          const updatedProducts = settings.products.map(p =>
                            p.id === product.id ? { ...p, custom_price: parsedValue } : p
                          );
                          setSettings(prev => ({ ...prev, products: updatedProducts }));
                        }}
                        size="small"
                        sx={{ width: 140 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">£</InputAdornment>
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        £{product.default_price}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEnabled ? (
                      <TextField
                        type="number"
                        value={enabledProduct?.duration_minutes !== undefined ? enabledProduct.duration_minutes : product.duration_minutes}
                        onChange={(e) => {
                          const value = e.target.value === '' ? '' : parseInt(e.target.value);
                          const updatedProducts = settings.products.map(p =>
                            p.id === product.id ? { ...p, duration_minutes: value } : p
                          );
                          setSettings(prev => ({ ...prev, products: updatedProducts }));
                        }}
                        onBlur={(e) => {
                          const value = e.target.value === '' ? product.duration_minutes : parseInt(e.target.value);
                          const parsedValue = isNaN(value) ? product.duration_minutes : (value <= 0 ? product.duration_minutes : value);
                          const updatedProducts = settings.products.map(p =>
                            p.id === product.id ? { ...p, duration_minutes: parsedValue } : p
                          );
                          setSettings(prev => ({ ...prev, products: updatedProducts }));
                        }}
                        size="small"
                        sx={{ width: 130 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">mins</InputAdornment>
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {product.duration_minutes} mins
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={isEnabled ? 'Enabled' : 'Disabled'}
                      color={isEnabled ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={isEnabled}
                      onChange={() => handleProductToggle(product.id)}
                      color="primary"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
    
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
          Products Configuration
        </Typography>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={selectedCategory} 
            onChange={(e, value) => setSelectedCategory(value)}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500
              }
            }}
          >
            {categories.map(category => (
              <Tab 
                key={category}
                value={category}
                label={category === 'all' ? 'All Products' : category}
              />
            ))}
          </Tabs>
        </Box>
        
        {filteredProducts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ProductsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No products found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedCategory === 'all' ? 'No products available' : `No products in ${selectedCategory} category`}
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filteredProducts.length} product{filteredProducts.length === 1 ? '' : 's'} found
            </Typography>
            
            {isMobile ? (
              <Box>
                {filteredProducts.map(renderMobileCard)}
              </Box>
            ) : (
              renderDesktopTable()
            )}
          </>
        )}
      </Box>
    );
  };

  const renderAddonsTab = () => {
    const renderAddonIncludes = (includes, addonId) => {
      if (!includes || includes.length === 0) return null;
      
      const isExpanded = expandedIncludes[addonId];
      const displayIncludes = isExpanded ? includes : includes.slice(0, 2);
      
      return (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="primary.main" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
            What's included:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            {displayIncludes.map((include, index) => (
              <Chip
                key={index}
                label={include}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ 
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  borderColor: 'primary.main',
                  color: 'primary.main'
                }}
              />
            ))}
            {includes.length > 2 && (
              <Chip
                label={isExpanded ? 'Less' : `+${includes.length - 2} more...`}
                size="small"
                variant="outlined"
                color="info"
                onClick={() => setExpandedIncludes(prev => ({ ...prev, [addonId]: !prev[addonId] }))}
                sx={{ 
                  fontSize: '0.75rem', 
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'info.50' }
                }}
              />
            )}
          </Box>
        </Box>
      );
    };

    const renderMobileAddonCard = (addon) => {
      const isEnabled = settings.addons.some(a => a.id === addon.id && a.enabled);
      const enabledAddon = settings.addons.find(a => a.id === addon.id);
      
      return (
        <Card key={addon.id} variant="outlined" sx={{ 
          mb: 2,
          backgroundColor: isEnabled ? 'primary.50' : 'background.paper',
          border: '1px solid',
          borderColor: isEnabled ? 'primary.light' : 'divider',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: theme.shadows[4],
            borderColor: isEnabled ? 'primary.main' : 'primary.light'
          }
        }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, color: 'white' }}>
                <AddonsIcon sx={{ color: 'white' }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {addon.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {addon.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  £{addon.default_price} • {addon.duration_minutes || 0} mins
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip
                    label={isEnabled ? 'Enabled' : 'Disabled'}
                    color={isEnabled ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                {renderAddonIncludes(addon.includes, addon.id)}
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
              <Switch
                checked={isEnabled}
                onChange={() => handleAddonToggle(addon.id)}
                color="primary"
              />
            </Box>
            
            {isEnabled && (
              <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Custom Price"
                  type="number"
                  value={enabledAddon?.custom_price !== undefined ? enabledAddon.custom_price : addon.default_price}
                  onChange={(e) => {
                    const value = e.target.value === '' ? '' : parseFloat(e.target.value);
                    const updatedAddons = settings.addons.map(a =>
                      a.id === addon.id ? { ...a, custom_price: value } : a
                    );
                    setSettings(prev => ({ ...prev, addons: updatedAddons }));
                  }}
                  onBlur={(e) => {
                    const value = e.target.value === '' ? addon.default_price : parseFloat(e.target.value);
                    const parsedValue = isNaN(value) ? addon.default_price : (value < 0 ? addon.default_price : value);
                    const updatedAddons = settings.addons.map(a =>
                      a.id === addon.id ? { ...a, custom_price: parsedValue } : a
                    );
                    setSettings(prev => ({ ...prev, addons: updatedAddons }));
                  }}
                  size="small"
                  sx={{ flex: 1, minWidth: isMobile ? '120px' : '250px' }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Duration (mins)"
                  type="number"
                  value={enabledAddon?.duration_minutes !== undefined ? enabledAddon.duration_minutes : addon.duration_minutes}
                  onChange={(e) => {
                    const value = e.target.value === '' ? '' : parseInt(e.target.value);
                    const updatedAddons = settings.addons.map(a =>
                      a.id === addon.id ? { ...a, duration_minutes: value } : a
                    );
                    setSettings(prev => ({ ...prev, addons: updatedAddons }));
                  }}
                  onBlur={(e) => {
                    const value = e.target.value === '' ? addon.duration_minutes : parseInt(e.target.value);
                    const parsedValue = isNaN(value) ? addon.duration_minutes : (value < 0 ? addon.duration_minutes : value);
                    const updatedAddons = settings.addons.map(a =>
                      a.id === addon.id ? { ...a, duration_minutes: parsedValue } : a
                    );
                    setSettings(prev => ({ ...prev, addons: updatedAddons }));
                  }}
                  size="small"
                  sx={{ flex: 1, minWidth: isMobile ? '120px' : '250px' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">mins</InputAdornment>
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      );
    };

    const renderDesktopAddonTable = () => (
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Add-on Name</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(Array.isArray(availableAddons) ? availableAddons : []).map((addon) => {
              const isEnabled = settings.addons.some(a => a.id === addon.id && a.enabled);
              const enabledAddon = settings.addons.find(a => a.id === addon.id);
              
              return (
                <TableRow key={addon.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                        <AddonsIcon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2">{addon.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {addon.description}
                        </Typography>
                        {renderAddonIncludes(addon.includes, addon.id)}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {isEnabled ? (
                      <TextField
                        type="number"
                        value={enabledAddon?.custom_price !== undefined ? enabledAddon.custom_price : addon.default_price}
                        onChange={(e) => {
                          const value = e.target.value === '' ? '' : parseFloat(e.target.value);
                          const updatedAddons = settings.addons.map(a =>
                            a.id === addon.id ? { ...a, custom_price: value } : a
                          );
                          setSettings(prev => ({ ...prev, addons: updatedAddons }));
                        }}
                        onBlur={(e) => {
                          const value = e.target.value === '' ? addon.default_price : parseFloat(e.target.value);
                          const parsedValue = isNaN(value) ? addon.default_price : (value < 0 ? addon.default_price : value);
                          const updatedAddons = settings.addons.map(a =>
                            a.id === addon.id ? { ...a, custom_price: parsedValue } : a
                          );
                          setSettings(prev => ({ ...prev, addons: updatedAddons }));
                        }}
                        size="small"
                        sx={{ width: 140 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">£</InputAdornment>
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        £{addon.default_price}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEnabled ? (
                      <TextField
                        type="number"
                        value={enabledAddon?.duration_minutes !== undefined ? enabledAddon.duration_minutes : addon.duration_minutes}
                        onChange={(e) => {
                          const value = e.target.value === '' ? '' : parseInt(e.target.value);
                          const updatedAddons = settings.addons.map(a =>
                            a.id === addon.id ? { ...a, duration_minutes: value } : a
                          );
                          setSettings(prev => ({ ...prev, addons: updatedAddons }));
                        }}
                        onBlur={(e) => {
                          const value = e.target.value === '' ? addon.duration_minutes : parseInt(e.target.value);
                          const parsedValue = isNaN(value) ? addon.duration_minutes : (value < 0 ? addon.duration_minutes : value);
                          const updatedAddons = settings.addons.map(a =>
                            a.id === addon.id ? { ...a, duration_minutes: parsedValue } : a
                          );
                          setSettings(prev => ({ ...prev, addons: updatedAddons }));
                        }}
                        size="small"
                        sx={{ width: 130 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">mins</InputAdornment>
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {addon.duration_minutes || 0} mins
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={isEnabled ? 'Enabled' : 'Disabled'}
                      color={isEnabled ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={isEnabled}
                      onChange={() => handleAddonToggle(addon.id)}
                      color="primary"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );

    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
          Add-ons Configuration
        </Typography>
        
        {(!Array.isArray(availableAddons) || availableAddons.length === 0) ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <AddonsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No add-ons found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No add-ons are available for configuration
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {Array.isArray(availableAddons) ? availableAddons.length : 0} add-on{(Array.isArray(availableAddons) ? availableAddons.length : 0) === 1 ? '' : 's'} available
            </Typography>
            
            {isMobile ? (
              <Box>
                {(Array.isArray(availableAddons) ? availableAddons : []).map(renderMobileAddonCard)}
              </Box>
            ) : (
              renderDesktopAddonTable()
            )}
          </>
        )}
      </Box>
    );
  };

  const renderPricingTab = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
        Pricing Rules Configuration
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item size={{ xs: 12, md: 4 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Discount Percentage
              </Typography>
              <TextField
                type="number"
                value={settings.pricingConfig.discount_percentage}
                onChange={(e) => handlePricingConfigChange('discount_percentage', e.target.value)}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item size={{ xs: 12, md: 4 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Weekend Multiplier
              </Typography>
              <TextField
                type="number"
                step="0.1"
                value={settings.pricingConfig.weekend_multiplier}
                onChange={(e) => handlePricingConfigChange('weekend_multiplier', e.target.value)}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">x</InputAdornment>
                }}
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item size={{ xs: 12, md: 4 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Urgent Booking Multiplier
              </Typography>
              <TextField
                type="number"
                step="0.1"
                value={settings.pricingConfig.urgent_booking_multiplier}
                onChange={(e) => handlePricingConfigChange('urgent_booking_multiplier', e.target.value)}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">x</InputAdornment>
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderBookingConfigTab = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
        Booking Configuration
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Accept Bookings
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.bookingConfig.accept_booking}
                    onChange={(e) => handleBookingConfigChange('accept_booking', e.target.checked)}
                    color="primary"
                  />
                }
                label={settings.bookingConfig.accept_booking ? "Accepting bookings" : "Not accepting bookings"}
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Advance Booking Days
              </Typography>
              <TextField
                type="number"
                value={settings.bookingConfig.advance_booking_days}
                onChange={(e) => handleBookingConfigChange('advance_booking_days', e.target.value)}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">days</InputAdornment>
                }}
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Cancellation Window
              </Typography>
              <TextField
                type="number"
                value={settings.bookingConfig.cancellation_window_hours}
                onChange={(e) => handleBookingConfigChange('cancellation_window_hours', e.target.value)}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">hours</InputAdornment>
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderNotificationsTab = () => (
    <Box sx={{ p: 0 }}>
      <NotificationSettings 
        garageId={garage?.id} 
        configs={settings.notificationConfigs}
        onConfigChange={(configs) => setSettings(prev => ({ ...prev, notificationConfigs: configs }))}
      />
    </Box>
  );

  const tabContent = [
    renderOpeningHoursTab(),
    renderProductsTab(),
    renderAddonsTab(),
    renderPricingTab(),
    renderBookingConfigTab(),
    renderNotificationsTab()
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            height: isMobile ? '100%' : '90vh',
            maxHeight: isMobile ? '100%' : '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
              Garage Settings - {garage?.name}
            </Typography>
            <IconButton onClick={onClose} color="primary">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 60,
                  textTransform: 'none',
                  fontWeight: 500
                }
              }}
            >
              <Tab 
                icon={<TimeIcon />} 
                label="Opening Hours" 
                sx={{ color: 'text.primary' }}
              />
              <Tab 
                icon={<ProductsIcon />} 
                label="Products" 
                sx={{ color: 'text.primary' }}
              />
              <Tab 
                icon={<AddonsIcon />} 
                label="Add-ons" 
                sx={{ color: 'text.primary' }}
              />
              <Tab 
                icon={<PricingIcon />} 
                label="Pricing" 
                sx={{ color: 'text.primary' }}
              />
              <Tab 
                icon={<ScheduleIcon />} 
                label="Booking Config" 
                sx={{ color: 'text.primary' }}
              />
              <Tab 
                icon={<NotificationsIcon />} 
                label="Notifications" 
                sx={{ color: 'text.primary' }}
              />
            </Tabs>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              tabContent[activeTab]
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper'
        }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      {success && (
        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Snackbar>
      )}

      {error && (
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default GarageSettingsModal;